import React, { useRef, useEffect } from 'react';
import styled from 'styled-components/macro';
import { useMonth, FirstDayOfWeek } from '@datepicker-react/hooks';
import { ActionsContainer } from './ActionsContainer';
import NavButton from './NavButton';
import Day from './Day';
import { LeftArrowIcon, RightArrowIcon } from 'assets/icons';

interface IMonthProps {
  year: number;
  month: number;
  firstDayOfWeek: FirstDayOfWeek;
  currentDay: Date | null;
  goToPreviousMonths: () => void;
  goToNextMonths: () => void;
  onDaySelected?: () => void;
  scrollIntoView?: boolean;
}
const Month = ({
  year,
  month,
  firstDayOfWeek,
  currentDay,
  goToPreviousMonths,
  goToNextMonths,
  onDaySelected,
  scrollIntoView,
}: IMonthProps): JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { days, weekdayLabels, monthLabel } = useMonth({
    year,
    month,
    firstDayOfWeek,
  });

  useEffect(() => {
    if (scrollIntoView) ref.current?.scrollIntoView();
  }, [scrollIntoView]);

  return (
    <div ref={ref}>
      <ActionsContainer>
        <NavButton
          data-mixpanel-name="Previous month on calendar"
          data-testid="previous-month-button"
          onClick={goToPreviousMonths}
        >
          <LeftArrowIcon />
        </NavButton>
        <MonthLabel>{monthLabel}</MonthLabel>
        <NavButton data-mixpanel-name="Next month on calendar" data-testid="next-month-button" onClick={goToNextMonths}>
          <RightArrowIcon />
        </NavButton>
      </ActionsContainer>
      <ContentContainer>
        <WeekGrid>
          {weekdayLabels.map((dayLabel) => (
            <WeekGridItem key={dayLabel}>{dayLabel}</WeekGridItem>
          ))}
        </WeekGrid>
        <DayGrid>
          {days.map((day, index) => {
            if (typeof day === 'object') {
              return (
                <Day
                  key={day.date.toString()}
                  date={day.date}
                  dayLabel={day.dayLabel}
                  currentDay={currentDay}
                  onDaySelected={onDaySelected}
                />
              );
            }
            return <div key={index} />;
          })}
        </DayGrid>
      </ContentContainer>
    </div>
  );
};

export default Month;

const MonthLabel = styled.span`
  color: #020d6e;
  text-align: center;
  font-size: 18px;
`;

const ContentContainer = styled.div`
  padding: 0.5em 0.5em 0 0.5em;
`;

const WeekGrid = styled.div`
  color: hsl(233, 4%, 42%);
  display: grid;
  font-size: 13px;
  justify-content: center;
  grid-template-columns: repeat(7, 1fr);
  margin: 8px 0 10px;
`;

const WeekGridItem = styled.div`
  text-align: center;
`;

const DayGrid = styled.div`
  display: grid;
  justify-content: center;
  grid-template-columns: repeat(7, 1fr);
`;
